/*
  This module allows interacting with msal (Microsoft Authentication Library),
  which we use to authenticate users.
*/

import { createContext } from "react";
import { FETCH_USER } from "./actionTypes";
import axios from "axios";
import { UserAgentApplication, Logger, LogLevel } from "msal";

/**
 * Use this in components which want to use an instance of MsalWrapper:
 * const msalWrapper = useContext(MsalWrapperContext);
 * msalWrapper.onSignIn();
 */
export const MsalWrapperContext = createContext({});

/**
 * Only one place should create an instance of this class.
 * Components should use React's useContext with MsalWrapperContext if they want to use an instance.
 */
export class MsalWrapper {
  #appConfig;
  #msalApp;

  constructor(appConfig) {
    this.#appConfig = appConfig;

    const isIE = () => {
      const ua = window.navigator.userAgent;
      const msie = ua.indexOf("MSIE ") > -1;
      const msie11 = ua.indexOf("Trident/") > -1;
    
      // If you as a developer are testing using Edge InPrivate mode, please add "isEdge" to the if check
      // const isEdge = ua.indexOf("Edge/") > -1;
    
      return msie || msie11;
    };
    const msalConfig = {
      auth: {
        clientId: `${appConfig.REACT_APP_B2C_APPLICATION_ID}`,
        authority: `${appConfig.REACT_APP_B2C_AUTHORITY}/${appConfig.REACT_APP_B2C_TENANT}/${appConfig.REACT_APP_B2C_SIGNIN_POLICY}`,
        validateAuthority: false,
        postLogoutRedirectUri: `${appConfig.REACT_APP_B2C_POST_LOGOUT_URI}`,
        navigateToLoginRequestUrl: true,
        redirectUri: `${appConfig.REACT_APP_B2C_REDIRECT_URI}`,
      },
      cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: isIE(),
      },
      system: {
        navigateFrameWait: 0,
        logger: new Logger(
          (logLevel, message) => {
            console.log(message);
          },
          {
            level: LogLevel.Verbose,
            piiLoggingEnabled: true,
          }
        ),
        telemetry: {
          applicationName: "HWTS",
          applicationVersion: "1.0.0",
          telemetryEmitter: (events) => {
            console.log("Telemetry Events:", events);
          },
        },
      },
    };
    this.#msalApp = new UserAgentApplication(msalConfig);
    this.#msalApp.handleRedirectCallback((error) => {
      if (error) {
        if (error.errorMessage.indexOf("AADB2C90118") > -1) {
          try {
            // Password reset policy/authority
            this.#msalApp.loginRedirect(this.#b2cPolicies_authorities_forgotPassword);
          } catch (err) {
            console.log(err);
          }
        }
      }
    });
  }

  get #b2cPolicies_authorities_forgotPassword() {
    const result = {
      authority: `${this.#appConfig.REACT_APP_B2C_AUTHORITY}/${this.#appConfig.REACT_APP_B2C_TENANT}/${this.#appConfig.REACT_APP_B2C_RESET_POLICY}`,
    };
    return result;
  }

  fetchUser() {
    return (dispatch) => {
      const account = this.#msalApp.getAccount();

      if (account) {
        axios.get(`api/v1/hwts/roles`).then((res) => {
          dispatch({
            type: FETCH_USER,
            payload: {
              isFetching: false,
              isAuth: true,
              account: account,
              roles: res.data,
            },
          });
        });
      } else {
        dispatch({
          type: FETCH_USER,
          payload: {
            isFetching: false,
            isAuth: false,
            account: {},
            roles: [],
          },
        });
      }
    };
  }

  onSignIn() {
    const GRAPH_REQUESTS_LOGIN = {
      scopes: [`${this.#appConfig.REACT_APP_B2C_SCOPES}`],
    };
    this.#msalApp.loginRedirect(GRAPH_REQUESTS_LOGIN);
  }

  onResetPassword() {
    this.#msalApp.loginRedirect(this.#b2cPolicies_authorities_forgotPassword);
  }

  onSignOut() {
    this.#msalApp.logout();
  }
}
